import { keyBy, startsWith } from 'lodash';
import { useCallback, useMemo } from 'react';
import { ExportFileField } from './ExportFileTypes';
import { camelCaseToWords } from './useExportFileFieldSelectOptions';

type Direction = 'asc' | 'desc';

interface SemanticOrder {
  key: string;
  label: string;
  direction: Direction;
}

interface UseExportFileSemanticOrdersParams {
  fields: ExportFileField[] | null | undefined;
  selected?: string[];
  value?: string[];
  onChange: (value: string[]) => void;
}

/**
 * Hook per la gestione degli ordinamenti sui campi semantici
 */
export function useExportFileSemanticOrders(
  params: UseExportFileSemanticOrdersParams,
) {
  const { fields, selected, value: rawValue, onChange } = params;

  const selectedSet = useMemo(() => new Set(selected ?? []), [selected]);

  const value = useMemo(
    () => (Array.isArray(rawValue) ? rawValue : []),
    [rawValue],
  );

  const selectableMap = useMemo(() => {
    const filtered = (fields ?? []).filter(
      field => field.chiave && selectedSet.has(field.chiave),
    );
    return keyBy(filtered, 'chiave') as Record<string, ExportFileField>;
  }, [fields, selectedSet]);

  const orders = useMemo(() => {
    if (!value.length) return [];

    return value
      .map(item => {
        const key = startsWith(item, '-') ? item.slice(1) : item;
        const field = selectableMap[key];
        if (!field) return null;

        return {
          key,
          label: field.titolo ?? camelCaseToWords(field.chiave!) ?? '',
          direction: startsWith(item, '-') ? 'desc' : 'asc',
        } satisfies SemanticOrder;
      })
      .filter(order => order != null);
  }, [value, selectableMap]);

  const options = useMemo(() => {
    const uniqueKeys = new Set(orders.map(({ key }) => key));

    return Object.values(selectableMap)
      .filter(field => field.chiave && !uniqueKeys.has(field.chiave))
      .map(field => ({
        value: field.chiave!,
        label: field.titolo ?? camelCaseToWords(field.chiave!) ?? '',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [orders, selectableMap]);

  const updateOrders = useCallback(
    (next: SemanticOrder[]) => {
      const payload = next.map(order =>
        order.direction === 'desc' ? `-${order.key}` : order.key,
      );
      onChange(payload);
    },
    [onChange],
  );

  const setDirection = useCallback(
    (key: string, direction: Direction) => {
      updateOrders(
        orders.map(order =>
          order.key === key ? { ...order, direction } : order,
        ),
      );
    },
    [orders, updateOrders],
  );

  const removeOrder = useCallback(
    (key: string) => updateOrders(orders.filter(order => order.key !== key)),
    [orders, updateOrders],
  );

  const addOrder = useCallback(
    (key?: string) => {
      if (!key || !selectableMap[key] || orders.some(o => o.key === key)) {
        return;
      }

      updateOrders([
        ...orders,
        {
          key,
          label:
            selectableMap[key].titolo ??
            camelCaseToWords(selectableMap[key].chiave!) ??
            '',
          direction: 'asc',
        },
      ]);
    },
    [orders, selectableMap, updateOrders],
  );

  return {
    orders,
    options,
    addOrder,
    removeOrder,
    setDirection,
  };
}
